import { useEffect, useMemo, useCallback } from 'react';
import { Platform } from 'react-native';
import { useQueryClient } from '@tanstack/react-query';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  useMeetingAnnouncements,
  meetingAnnouncementKeys,
} from './useMeetingAnnouncements';
import { profileKeys } from './useProfile';
import { useMyProfile } from './useProfile';
import apiClient from '../client';
import type {
  MeetingAnnouncementSearchParams,
  MeetingAnnouncementView,
  MeetingAnnouncementPhotoView,
  UserPublicProfileView,
  ApiPlatformCollectionResponse,
} from '../types';
import { extractCollectionItems } from '../types';
import type { Profile } from '@/data/profiles';
import {
  mapAnnouncementToProfile,
  extractPhotoUrls,
} from '../mappers/announcementMappers';

const PREFETCH_COUNT = 3; // Buffer current + 2 next profiles

interface UsePrefetchedAnnouncementsOptions {
  /** Search/filter parameters */
  params?: MeetingAnnouncementSearchParams;
  /** Current index in the list (for prefetching) */
  currentIndex?: number;
  /** Enable/disable the query */
  enabled?: boolean;
  /** Exclude own announcements from the list */
  excludeOwn?: boolean;
}

interface UsePrefetchedAnnouncementsResult {
  /** Mapped profiles ready for SwipeCard */
  profiles: Profile[];
  /** Raw announcements from API */
  announcements: MeetingAnnouncementView[];
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  isError: boolean;
  /** Error object */
  error: Error | null;
  /** Refetch function */
  refetch: () => void;
  /** Check if photos are loaded for an announcement */
  hasPhotosLoaded: (announcementId: string) => boolean;
  /** Get photos for an announcement (from cache) */
  getPhotosForAnnouncement: (
    announcementId: string,
  ) => MeetingAnnouncementPhotoView[] | undefined;
  /** Get user profile for an announcement creator (from cache) */
  getUserProfileForAnnouncement: (
    announcementId: string,
  ) => UserPublicProfileView | undefined;
}

/**
 * Hook that fetches meeting announcements with photo prefetching
 * Prefetches photos for the next PREFETCH_COUNT announcements
 */
export const usePrefetchedAnnouncements = (
  options: UsePrefetchedAnnouncementsOptions = {},
): UsePrefetchedAnnouncementsResult => {
  const {
    params,
    currentIndex = 0,
    enabled = true,
    excludeOwn = true,
  } = options;
  const queryClient = useQueryClient();

  // Fetch current user's profile to filter own announcements
  const { data: myProfile } = useMyProfile({ enabled: excludeOwn });

  // Fetch announcements list
  const {
    data: announcements = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useMeetingAnnouncements(params, { enabled });

  // Ensure announcements is always an array and filter out own announcements
  const safeAnnouncements = useMemo(() => {
    const list = Array.isArray(announcements) ? announcements : [];
    if (excludeOwn && myProfile?.id) {
      return list.filter(a => a.createdById !== myProfile.id);
    }
    return list;
  }, [announcements, excludeOwn, myProfile?.id]);

  // Prefetch photos and user profiles for upcoming announcements
  useEffect(() => {
    if (!safeAnnouncements.length || !enabled) return;

    const startIndex = currentIndex;
    const endIndex = Math.min(
      currentIndex + PREFETCH_COUNT,
      safeAnnouncements.length,
    );

    for (let i = startIndex; i < endIndex; i++) {
      const announcement = safeAnnouncements[i];
      if (!announcement) continue;

      // Prefetch photo images to browser cache
      if (Platform.OS === 'web') {
        const photosToPreload = announcement.photos || [];
        const creatorPhoto = announcement.createdByPhotoThumbnail;
        
        // Preload with authorization if needed
        const preloadImage = async (url: string) => {
          if (!url) return;
          
          if (url.includes('tino-api.grupa-abs.pl')) {
            // Authenticated URL - fetch with token
            try {
              const token = await AsyncStorage.getItem('@tinomobile:auth_token');
              if (token) {
                fetch(url, {
                  headers: { Authorization: `Bearer ${token}` },
                  mode: 'cors',
                  credentials: 'omit',
                });
              }
            } catch (e) {
              // Ignore prefetch errors
            }
          } else {
            // Public URL - use Image
            const img = new Image();
            img.src = url;
          }
        };
        
        // Preload announcement photos
        photosToPreload.forEach(photo => {
          if (photo.downloadUrl) {
            preloadImage(photo.downloadUrl);
          }
        });
        
        // Preload creator thumbnail
        if (creatorPhoto) {
          preloadImage(creatorPhoto);
        }
      }

      // Prefetch user profile (for age, isVerified, etc.)
      if (announcement.createdById) {
        const profileQueryKey = profileKeys.public(announcement.createdById);
        const existingProfileData = queryClient.getQueryData(profileQueryKey);
        if (!existingProfileData) {
          queryClient.prefetchQuery({
            queryKey: profileQueryKey,
            queryFn: async () => {
              const response = await apiClient.get<UserPublicProfileView>(
                `/users/${announcement.createdById}/profile`,
              );
              return response.data;
            },
            staleTime: 5 * 60 * 1000, // 5 minutes
          });
        }
      }
    }
  }, [safeAnnouncements, currentIndex, enabled, queryClient]);

  // Photos are now included in announcement.photos - no separate query needed
  const hasPhotosLoaded = useCallback(
    (announcementId: string): boolean => {
      const announcement = safeAnnouncements.find(a => a.id === announcementId);
      return !!announcement?.photos;
    },
    [safeAnnouncements],
  );

  // Get photos from announcement object
  const getPhotosForAnnouncement = useCallback(
    (announcementId: string): MeetingAnnouncementPhotoView[] | undefined => {
      const announcement = safeAnnouncements.find(a => a.id === announcementId);
      return announcement?.photos;
    },
    [safeAnnouncements],
  );

  // Get user profile from cache for a specific announcement
  const getUserProfileForAnnouncement = useCallback(
    (announcementId: string): UserPublicProfileView | undefined => {
      const announcement = safeAnnouncements.find(a => a.id === announcementId);
      if (!announcement?.createdById) return undefined;
      const queryKey = profileKeys.public(announcement.createdById);
      return queryClient.getQueryData(queryKey);
    },
    [queryClient, safeAnnouncements],
  );

  // Map announcements to profiles with cached photos and user profiles
  const profiles = useMemo(() => {
    if (!Array.isArray(safeAnnouncements)) return [];
    return safeAnnouncements.map(announcement => {
      const photos = getPhotosForAnnouncement(announcement.id);
      const userProfile = getUserProfileForAnnouncement(announcement.id);
      return mapAnnouncementToProfile(announcement, photos, userProfile);
    });
  }, [
    safeAnnouncements,
    getPhotosForAnnouncement,
    getUserProfileForAnnouncement,
  ]);

  return {
    profiles,
    announcements: safeAnnouncements,
    isLoading,
    isError,
    error: error as Error | null,
    refetch,
    hasPhotosLoaded,
    getPhotosForAnnouncement,
    getUserProfileForAnnouncement,
  };
};

/**
 * Hook to get a single announcement with photos
 */
export const useAnnouncementWithPhotos = (
  announcementId: string | undefined,
) => {
  const queryClient = useQueryClient();

  // Get announcement from cache or fetch
  const announcementQueryKey = meetingAnnouncementKeys.detail(
    announcementId || '',
  );

  const announcement = useMemo(() => {
    if (!announcementId) return undefined;

    // Try to get from list cache first
    const lists = queryClient.getQueriesData<MeetingAnnouncementView[]>({
      queryKey: meetingAnnouncementKeys.lists(),
    });

    for (const [, data] of lists) {
      if (data) {
        const found = data.find(a => a.id === announcementId);
        if (found) return found;
      }
    }

    return undefined;
  }, [announcementId, queryClient]);

  // Photos are included in announcement.photos
  const photos = announcement?.photos;

  // Map to profile if we have the data
  const profile = useMemo(() => {
    if (!announcement) return undefined;
    return mapAnnouncementToProfile(announcement);
  }, [announcement]);

  return {
    announcement,
    photos,
    profile,
    photoUrls: photos ? extractPhotoUrls(photos) : [],
  };
};

export default usePrefetchedAnnouncements;
